import type { ReactNode } from 'react';
import { brandCss, DEFAULT_PRODUCT_NAME, type BrandDocument } from '../brand/brand';
import { PREVIEW_BRANDS } from '../brand/previewBrands';

/**
 * The firm's brand on the public intake page, injected and never persisted
 * (docs/25 §3 and security checklist §7).
 *
 * This page is pre-auth and anyone can open it, so brand handling here is deliberately
 * one-directional. It reads a compiled-in brand document from `PREVIEW_BRANDS`
 * (`src/brand/previewBrands.ts`) — since steward-app#664 retired the `?brand=` query
 * preview, this is the ONLY consumer of those constants, which is why that file
 * survived the retirement — and writes nothing: no localStorage, no flash cache, no
 * favicon swap. A visitor who lands on a firm's intake link therefore cannot leave a
 * brand behind that would paint a later, unrelated visit to this browser. The
 * persistent, host-keyed cache stays the business of `BrandProvider`, which only ever
 * fills it from a host the firm actually owns.
 *
 * Injection is a plain `<style>` element rendered by React inside the page rather than
 * a call into `applyBrand`: that keeps the tokens tied to this component's lifetime
 * (they leave when the page does), and it cannot race `BrandProvider`, which owns the
 * `#brand-tokens` element in `<head>` and would otherwise overwrite a page-level
 * brand on its own first effect.
 */

export interface IntakeBrand {
  doc: BrandDocument | null;
  /** Short mark for the lockup and the submit button ("RIPL"). */
  shortName: string;
  /** Eyebrow above the wordmark, when the brand supplies one. */
  kicker: string | null;
}

/**
 * The firm's brand document, from the compiled brand presets. `null` means the page
 * renders in Steward's own colours, which is a correct outcome, not a failure.
 * `Object.hasOwn` so a brand key can never resolve to an inherited Object member.
 */
export function intakeBrand(brandKey: string, firmName: string): IntakeBrand {
  const doc = Object.hasOwn(PREVIEW_BRANDS, brandKey) ? PREVIEW_BRANDS[brandKey] ?? null : null;
  return {
    doc,
    shortName: doc?.wordmark?.text ?? doc?.productName ?? firmName ?? DEFAULT_PRODUCT_NAME,
    kicker: doc?.wordmark?.kicker ?? null,
  };
}

/**
 * The name a firm goes by on a client's page: the brand's short name ("RIPL"), else the firm's own
 * name, else `null`. It is `intakeBrand(...).shortName`, the string the snapshot form's header
 * prints, so the onboarding form's header and both forms' client notice name the firm one way
 * (steward-app#2770). A brand key the presets do not know falls back to the firm's name.
 */
export function firmDisplayName(brandKey: string | undefined, firmName: string | null | undefined): string | null {
  const name = firmName?.trim() ? firmName.trim() : null;
  if (!brandKey) return name;
  const doc = Object.hasOwn(PREVIEW_BRANDS, brandKey) ? PREVIEW_BRANDS[brandKey] ?? null : null;
  return doc ? intakeBrand(brandKey, name ?? DEFAULT_PRODUCT_NAME).shortName : name;
}

export function IntakeBrandScope({ brand, children }: { brand: IntakeBrand; children: ReactNode }) {
  const fontsUrl = brand.doc?.fonts?.stylesheetUrl;
  return (
    <>
      {brand.doc && <style>{brandCss(brand.doc)}</style>}
      {/* The allowlist check that `applyBrand` applies to a server-sourced brand,
          applied to this one too: the only stylesheet host the page will reach for
          is the one the CSP permits anyway. */}
      {fontsUrl?.startsWith('https://fonts.googleapis.com/') && (
        <link rel="stylesheet" href={fontsUrl} />
      )}
      {children}
    </>
  );
}
